import React, { useEffect, useState } from 'react';
import { Button, Form, Modal, Spinner } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { aiMonitoringAPI } from '../../services';

/**
 * #181800 (lệnh sếp 18/10/2026) — CÀI ĐẶT NHẬN DIỆN của chi nhánh: ngưỡng % AI tin "có khách" để hệ thống
 * ghi nhận một vùng đã khoanh (lưu lịch sử + gửi tin Telegram). Thấp hơn ngưỡng thì bỏ qua. 0% = nhận mọi vùng.
 *
 * Lệnh sếp 18/10/2026 (sau sự cố trừ điểm oan) — thêm 2 CÔNG TẮC, mặc định TẮT, bấm là lưu ngay:
 * - "Gửi tin cho nhân viên": tin luôn vào kênh kiểm tra hoá đơn theo camera; bật thì gửi thêm riêng NV trong ca.
 * - "Phạt điểm khi quá hạn": tắt thì quá hạn chỉ nhắc; chỉ bật được khi đang gửi tin cho nhân viên.
 */
const AiCambillSettingsModal = ({ show, onHide, branchId, branchName, onSaved }) => {
  const [current, setCurrent] = useState(null);
  const [percent, setPercent] = useState('');
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [switches, setSwitches] = useState({ notify_nv_enabled: false, penalty_enabled: false });
  const [switching, setSwitching] = useState(false);

  useEffect(() => {
    if (!show || !branchId) return;
    let alive = true;
    setLoading(true);
    aiMonitoringAPI
      .getRegionSettings(branchId)
      .then((r) => {
        if (!alive) return;
        const v = Number(r?.min_region_confidence ?? 0);
        setCurrent(v);
        setPercent(String(Math.round(v * 100)));
        setSwitches({
          notify_nv_enabled: r?.notify_nv_enabled === true,
          penalty_enabled: r?.penalty_enabled === true,
        });
      })
      .catch((err) => alive && toast.error(err?.message || 'Không đọc được cài đặt'))
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, [show, branchId]);

  const n = Number(percent);
  const valid = percent !== '' && Number.isFinite(n) && n >= 0 && n <= 100;
  const dirty = valid && (current == null || Math.round(current * 100) !== n);

  const save = async () => {
    setBusy(true);
    try {
      await aiMonitoringAPI.setRegionSettings(branchId, n / 100);
      toast.success(`Đã đặt ngưỡng ${n}% — áp từ lượt quét kế tiếp.`);
      onSaved?.();
      onHide();
    } catch (err) {
      toast.error(err?.message || 'Không lưu được ngưỡng');
    } finally {
      setBusy(false);
    }
  };

  const toggle = async (field, value) => {
    if (
      field === 'penalty_enabled' &&
      value &&
      !window.confirm('Bật phạt: tin quá hạn không ai xác nhận sẽ trừ điểm nhân viên trong ca. Bật?')
    ) {
      return;
    }
    setSwitching(true);
    try {
      const r = await aiMonitoringAPI.setCambillSwitches(branchId, { [field]: value });
      setSwitches({
        notify_nv_enabled: r?.notify_nv_enabled === true,
        penalty_enabled: r?.penalty_enabled === true,
      });
      toast.success(value ? 'Đã bật.' : 'Đã tắt.');
    } catch (err) {
      toast.error(err?.message || 'Không lưu được công tắc');
    } finally {
      setSwitching(false);
    }
  };

  return (
    <Modal show={show} onHide={onHide} centered>
      <Modal.Header closeButton>
        <Modal.Title as="h5">Cài đặt nhận diện{branchName ? ` · ${branchName}` : ''}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {loading ? (
          <div className="text-center py-3"><Spinner size="sm" animation="border" /></div>
        ) : (
          <Form.Group>
            <Form.Label>Ngưỡng "Có khách" tối thiểu</Form.Label>
            <div className="d-flex align-items-center gap-2">
              <Form.Control
                type="number"
                inputMode="numeric"
                min={0}
                max={100}
                step={5}
                aria-label="Ngưỡng độ tin cậy (%)"
                value={percent}
                onChange={(e) => setPercent(e.target.value)}
                style={{ maxWidth: 120, fontSize: 16 }}
              />
              <span>%</span>
            </div>
            <Form.Text className="text-muted">
              Vùng đã khoanh mà AI tin có khách thấp hơn ngưỡng sẽ bị bỏ qua: không ghi vào lịch sử, không gửi
              tin Telegram. Đặt 0% để nhận mọi lượt.
            </Form.Text>
            {percent !== '' && !valid && <div className="small text-danger mt-1">Nhập số từ 0 đến 100.</div>}
          </Form.Group>
        )}
        {!loading && (
          <div className="mt-4 pt-3 border-top">
            <Form.Check
              type="switch"
              id="cambill-notify-nv"
              label="Gửi tin xác nhận cho nhân viên"
              checked={switches.notify_nv_enabled}
              disabled={switching}
              onChange={(e) => toggle('notify_nv_enabled', e.target.checked)}
            />
            <Form.Text className="text-muted d-block mb-3">
              Tin luôn gửi về kênh kiểm tra hoá đơn theo camera. Bật thì gửi thêm tin riêng (kèm link xác nhận)
              cho từng nhân viên đang trong ca.
            </Form.Text>
            <Form.Check
              type="switch"
              id="cambill-penalty"
              label="Phạt điểm khi quá hạn không ai xác nhận"
              checked={switches.penalty_enabled}
              disabled={switching || !switches.notify_nv_enabled}
              onChange={(e) => toggle('penalty_enabled', e.target.checked)}
            />
            <Form.Text className="text-muted d-block">
              {switches.notify_nv_enabled
                ? 'Tắt thì quá hạn chỉ nhắc, không trừ điểm. Bật thì trừ 1 điểm mỗi nhân viên trong ca (được giải trình).'
                : 'Bật "Gửi tin xác nhận cho nhân viên" trước — nhân viên chưa nhận tin thì không phạt.'}
            </Form.Text>
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="outline-secondary" onClick={onHide}>Đóng</Button>
        <Button onClick={save} disabled={!dirty || busy || loading}>
          {busy ? <Spinner size="sm" animation="border" /> : 'Lưu ngưỡng'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default AiCambillSettingsModal;
